import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import { Send, Users, CheckCircle, AlertCircle } from 'lucide-react';

export default function AdminMessaging() {
  const [target, setTarget] = useState('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const targetLabels = {
    all: 'All Users',
    all_owners: 'All Yacht Owners',
    all_helpers: 'All Helpers',
    verified_helpers: 'Verified Helpers Only',
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;

    setSending(true);
    setResult(null);
    try {
      const data = await adminAPI.sendBulkMessage({ target, title: title.trim(), body: body.trim() });
      setResult({ success: true, count: data.sent });
      setTitle('');
      setBody('');
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold text-navy-900">Messaging</h1>
        <p className="text-navy-500 mt-1">Send notifications to platform users.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSend} className="card space-y-5">
            <h2 className="text-lg font-display font-semibold text-navy-900">Compose Notification</h2>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">Target Audience</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(targetLabels).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTarget(key)}
                    className={`p-3 rounded-apple border text-sm font-medium text-left transition-all ${
                      target === key
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-navy-200 text-navy-600 hover:border-navy-300 hover:bg-navy-50'
                    }`}
                  >
                    <Users className={`h-4 w-4 mb-1 ${target === key ? 'text-primary-500' : 'text-navy-400'}`} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Notification title..."
                className="input-field w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">Message</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your message here..."
                className="input-field w-full"
                rows={6}
                required
              />
            </div>

            <button
              type="submit"
              disabled={sending || !title.trim() || !body.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <><div className="spinner w-4 h-4 border-white/30 border-t-white" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4" /> Send Notification</>
              )}
            </button>

            {result && (
              <div className={`flex items-center gap-2 p-3 rounded-apple ${
                result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                {result.success ? (
                  <><CheckCircle className="h-4 w-4" /> Sent to {result.count} user{result.count !== 1 ? 's' : ''} successfully!</>
                ) : (
                  <><AlertCircle className="h-4 w-4" /> {result.message || 'Failed to send'}</>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Preview */}
        <div>
          <div className="card">
            <h3 className="text-sm font-semibold text-navy-700 mb-3">Preview</h3>
            <div className="bg-navy-50 rounded-apple p-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Send className="h-4 w-4 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy-900">
                    {title || 'Notification Title'}
                  </p>
                  <p className="text-sm text-navy-600 mt-1">
                    {body || 'Your notification message will appear here...'}
                  </p>
                  <p className="text-xs text-navy-400 mt-2">Just now</p>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-navy-100">
              <p className="text-xs text-navy-500">
                Sending to: <span className="font-medium text-navy-700">{targetLabels[target]}</span>
              </p>
              <p className="text-xs text-navy-400 mt-1">
                Notifications will appear in-app and as push notifications for subscribed users.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
